import {
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Popover,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd"
import React, { useEffect, useState } from "react"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../../firebase/config"

import { CloseOutlined, FilterFilled, FilterOutlined } from "@ant-design/icons"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import ViewDoneListTechnical from "../../../components/Orders/Machines/ViewDoneListTechnical"
import { orderEndDate, orderStartDate } from "../../../components/Table/orderDateDisplay"
import { returnProperMachineTag } from "../../../components/utils/ReturnProperMachineTag"
import { MaterialVarianceValue } from "../../../utils/calcMaterialVariance"
import OrderTimestampsDrawer from "../../../components/Orders/OrderTimestampsDrawer"

const { Text, Link, Paragraph } = Typography

export default function DoneUnshippedList({ doneForMachine = "all" }) {
  const [data, setData] = useState([])
  const [shortColumn, setShortColumn] = useState(true)

  // Object to manage visibility of each column
  const [visibleColumns, setVisibleColumns] = useState({
    orderNumber: true,
    created: false,
    client: true,
    product: true,
    quantity: true,
    dieCut: true,
    material: true,
    height: true,
    width: true,
    plate: true,
    technical: true,
    completedTime: true,
    editDone: true,
    machine: true,
    variance: true,
  })
  const [filterActive, setFilterActive] = useState(false)

  //loading for while data is sorting
  const [isSorting, setIsSorting] = useState(true)

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  let materialFilter =
    data &&
    data.reduce((accumulator, item) => {
      const materialName = item.selectedProduct.material
      const existingItem = accumulator.find((filterItem) => filterItem.text === materialName)

      if (existingItem) {
        existingItem.value = materialName
      } else {
        accumulator.push({
          text: materialName,
          value: materialName,
        })
      }

      return accumulator
    }, [])

  let machineFilter =
    data &&
    data.reduce((accumulator, item) => {
      const machine = item.selectedProduct.machine
      const existingItem = accumulator.find((filterItem) => filterItem.text === machine)

      if (existingItem) {
        existingItem.value = machine
      } else {
        accumulator.push({
          text: machine,
          value: machine,
        })
      }

      return accumulator
    }, [])

  const tableCols = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      filterTitle: "Order Number",
      ellipsis: shortColumn ? { tooltip: true } : false,
      width: 220,
      key: "orderNumber",
      sorter: (a, b) => a.justOrderNumber - b.justOrderNumber,
      render: (value, record) => OrderAndPrefix(record),
    },

    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      key: "client",
      filterTitle: "Clients",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      ellipsis: shortColumn ? { tooltip: true } : false,
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      filterTitle: "Products",
      key: "product",
      width: "20%",
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amountCreated",
      align: "center",
      filterTitle: "Created Amount",
      key: "quantity",
      width: "6%",
      sorter: (a, b) => a.amountCreated - b.amountCreated,
      render: (value, record) => {
        if (record.selectedProduct.machine === "Tape") {
          return (
            <>
              <Text copyable={{ text: record.rollCount }}>{record.rollCount} Rolls</Text>
            </>
          )
        } else {
          return (
            <>
              <Popover
                content={
                  <>
                    <div style={{ padding: 5 }}>
                      <Descriptions column={1} style={{ width: 150 }}>
                        <Descriptions.Item label="Ordered">{record.labelCount}</Descriptions.Item>
                        <Descriptions.Item label="Created">{value}</Descriptions.Item>
                      </Descriptions>
                    </div>
                  </>
                }
                title="Amount details"
                trigger="click">
                <Link copyable={{ text: value }}>{value}</Link>
              </Popover>
            </>
          )
        }
      },
    },
    {
      title: "Variance",
      key: "variance",
      align: "center",
      filterTitle: "Variance",

      width: "8%",
      render: (_, record) => <MaterialVarianceValue order={record} />,
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      filters: machineFilter,
      onFilter: (value, record) => record.selectedProduct.machine.startsWith(value),
      filterSearch: true,
      filterTitle: "Machine",
      render: (value) => {
        return returnProperMachineTag(value)
      },
      key: "machine",
    },
    {
      title: "Die-cut",
      dataIndex: ["selectedProduct", "knifeCode"],
      align: "center",
      filterTitle: "Die Cut's",
      key: "dieCut",
      width: "8%",
    },
    {
      title: "Width",
      dataIndex: ["selectedProduct", "labelWidth"],
      align: "center",
      filterTitle: "Label's Width",
      width: "5%",
      key: "width",
    },
    {
      title: "Height",
      dataIndex: ["selectedProduct", "labelHeight"],
      align: "center",
      width: "5%",
      filterTitle: "Label's Height",
      key: "height",
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "center",
      filterTitle: "Materials",
      key: "material",
      width: "5%",
      ellipsis: shortColumn ? { tooltip: true } : false,
      filters: materialFilter,
      onFilter: (value, record) => record.selectedProduct.material.startsWith(value),
      filterSearch: true,
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Plate",
      dataIndex: ["selectedPlate", "height"],
      align: "center",
      filterTitle: "Plates",
      width: "5%",
      key: "plate",
      ellipsis: {
        showTitle: false,
      },
      render: (item) => {
        if (item) {
          return (
            <Tooltip placement="topLeft" title={item}>
              {item}
            </Tooltip>
          )
        } else {
          return <CloseOutlined style={{ color: "#f5222d" }} />
        }
      },
    },
    {
      title: "Technical",
      dataIndex: ["selectedProduct", "technicalPage"],
      key: "technical",
      filterTitle: "Technical Pages",
      align: "center",
      width: "6%",
      render: (value, record) => {
        if (value) {
          return (
            <>
              <ViewDoneListTechnical link={value} />
            </>
          )
        } else {
          return <CloseOutlined style={{ color: "#f5222d" }} />
        }
      },
    },
    {
      title: "Started",
      align: "center",
      dataIndex: "created",
      key: "created",
      filterTitle: "Creation Date",
      sorter: (a, b) => {
        const aTime = getTimestamp(a.created)
        const bTime = getTimestamp(b.created)
        return aTime - bTime
      },
      render: (value, record) => orderStartDate(value, record),
    },
    {
      title: "Ended",
      align: "center",
      dataIndex: "doneByRewinder",
      key: "completedTime",
      filterTitle: "Done date",
      sorter: (a, b) => {
        const aTime = getTimestamp(a.doneByRewinder)
        const bTime = getTimestamp(b.doneByRewinder)
        return aTime - bTime
      },
      render: (value, record) => (
        <OrderTimestampsDrawer order={record}>
          {orderEndDate(value, record)}
        </OrderTimestampsDrawer>
      ),
    },
  ]

  const getTimestamp = (dateValue) => {
    if (!dateValue) return 0

    // Firestore Timestamp (has seconds property)
    if (dateValue.seconds) {
      return dateValue.seconds
    }

    // Date object
    if (dateValue instanceof Date) {
      return dateValue.getTime() / 1000
    }

    // String
    if (typeof dateValue === "string") {
      return new Date(dateValue).getTime() / 1000
    }

    return 0
  }

  //get data
  //get data
  useEffect(() => {
    setIsSorting(true)
    const ordersRef = collection(db, "orders")

    // Query for orderStatus 6 (unshipped but done)
    const activeQ = query(ordersRef, where("orderStatus", "==", 6))

    const unsubscribe = onSnapshot(activeQ, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })

      // Filter out orders with the special UID
      const filteredResults = results.filter(
        (order) => order.uid !== "E1XNaWvT6PccXRSKB3JNlNehI362",
      )

      // Sort by doneByRewinder using the helper function
      const sortedResults = filteredResults.sort((a, b) => {
        const aTime = getTimestamp(a.doneByRewinder)
        const bTime = getTimestamp(b.doneByRewinder)
        return bTime - aTime // desc order (newest first)
      })

      setData(sortedResults)
      setIsSorting(false)
    })

    return () => unsubscribe()
  }, [])

  //handles the action for visibility change
  const handleVisibilityChange = (key, checked) => {
    setVisibleColumns((prev) => {
      const updatedVisibleColumns = { ...prev, [key]: checked }

      // Check if any columns are hidden to set filter active state
      const isAnyColumnHidden = Object.values(updatedVisibleColumns).includes(false)
      setFilterActive(isAnyColumnHidden)

      return updatedVisibleColumns
    })
  }

  //controls for handling the filter
  const columnVisibilityControls = (
    <div style={{ margin: -8, background: "white", borderRadius: "5px" }}>
      <Space direction="vertical" style={{ padding: 15 }}>
        {tableCols
          .filter((col) => !col.alwaysVisible)
          .map((col) => (
            <Checkbox
              key={col.key}
              checked={visibleColumns[col.key]}
              onChange={(e) => handleVisibilityChange(col.key, e.target.checked)}>
              {col.filterTitle}
            </Checkbox>
          ))}
      </Space>
    </div>
  )

  // This filters out columns that should NOT be visible
  const filteredColumns = tableCols.filter((column) => visibleColumns[column.key])

  return (
    <>
      <Paragraph italic style={{ color: "#8c8c8c" }}>
        Done list represents all the orders that are in "Warehouse" or "Winder"
      </Paragraph>
      <Space split={<Divider type="vertical" />}>
        <Popover
          color="#597ef7"
          placement="bottomLeft"
          trigger="click"
          content={columnVisibilityControls}>
          <Button
            type={filterActive ? "primary" : "dashed"}
            icon={filterActive ? <FilterFilled /> : <FilterOutlined />}>
            <Space>
              Filter Columns
              {filterActive ? (
                <Text style={{ color: "lightgray" }} italic>
                  (active)
                </Text>
              ) : (
                ""
              )}
            </Space>
          </Button>
        </Popover>
        <Space>
          <Text>Shorten columns?</Text>
          <Checkbox checked={shortColumn} onChange={(e) => setShortColumn(e.target.checked)} />
        </Space>
      </Space>
      <Table
        style={{ marginTop: 5 }}
        rowKey={(record) => record.id}
        size="small"
        pagination={{ pageSize: 500 }}
        dataSource={data}
        loading={isSorting}
        columns={filteredColumns}
      />
    </>
  )
}
